/**
 * Coordinate validation and extraction utilities for map components
 */

/**
 * Validates if a coordinate is a valid number within Earth's bounds
 * @param {number|string} coord - The coordinate to validate
 * @param {string} type - 'lat' or 'lng' for specific validation
 * @returns {boolean}
 */
export const isValidCoordinate = (coord, type = null) => {
  if (coord === null || coord === undefined) return false;
  
  const numCoord = parseFloat(coord);
  if (isNaN(numCoord)) return false;
  
  if (type === 'lat') {
    return numCoord >= -90 && numCoord <= 90;
  } else if (type === 'lng') {
    return numCoord >= -180 && numCoord <= 180;
  }
  
  // General validation for both lat and lng
  return numCoord >= -180 && numCoord <= 180;
};

/**
 * Extracts coordinates from a business location object with priority handling
 * @param {Object} businessLocation - Business location object
 * @param {Array} keywords - Keywords array for fallback center location
 * @returns {Object} { lat, lng, source } or null if no valid coordinates found
 */
export const extractBusinessCoordinates = (businessLocation, keywords = []) => {
  // Priority 1: Direct lat/lng properties
  if (isValidCoordinate(businessLocation?.lat, 'lat') && 
      isValidCoordinate(businessLocation?.lng, 'lng')) {
    return {
      lat: parseFloat(businessLocation.lat),
      lng: parseFloat(businessLocation.lng),
      source: 'direct properties'
    };
  }
  
  // Priority 2: coordinates object
  if (isValidCoordinate(businessLocation?.coordinates?.lat, 'lat') && 
      isValidCoordinate(businessLocation?.coordinates?.lng, 'lng')) {
    return {
      lat: parseFloat(businessLocation.coordinates.lat),
      lng: parseFloat(businessLocation.coordinates.lng),
      source: 'coordinates object'
    };
  }
  
  // Priority 3: center location from ranking data - improved search
  if (keywords.length > 0) {
    for (const keyword of keywords) {
      if (keyword?.testLocations && keyword.testLocations.length > 0) {
        // Try to find center location with multiple criteria
        let centerLocation = keyword.testLocations.find(
          loc => loc.isCenter === true
        );
        
        // Fallback: look for location named 'Firmenstandort' or 'Center'
        if (!centerLocation) {
          centerLocation = keyword.testLocations.find(
            loc => loc.locationName === 'Firmenstandort' || 
                   loc.locationName === 'Center'
          );
        }
        
        // Fallback: use first location if it has valid coordinates
        if (!centerLocation && keyword.testLocations[0]) {
          centerLocation = keyword.testLocations[0];
        }
        
        if (centerLocation && isValidCoordinate(centerLocation.lat, 'lat') && 
            isValidCoordinate(centerLocation.lng, 'lng')) {
          return {
            lat: parseFloat(centerLocation.lat),
            lng: parseFloat(centerLocation.lng),
            source: 'ranking center location'
          };
        }
      }
    }
  }
  
  return null;
};

/**
 * Gets coordinates without fallback - throws error if coordinates cannot be determined
 * @param {Object} businessLocation - Business location object
 * @param {Array} keywords - Keywords array for fallback center location
 * @returns {Object} { lat, lng, source } or throws Error
 */
export const getBusinessCoordinatesWithFallback = (businessLocation, keywords = []) => {
  const coordinates = extractBusinessCoordinates(businessLocation, keywords);
  
  if (coordinates) {
    return coordinates;
  }
  
  // No more silent fallback - throw descriptive error
  const businessName = businessLocation?.businessName || 'Unknown Business';
  const hasAddress = !!(businessLocation?.address?.formattedAddress || 
                       businessLocation?.address?.street || 
                       businessLocation?.address?.city);
  
  if (!hasAddress) {
    throw new Error(`Missing coordinates and address information for "${businessName}". Cannot determine business location.`);
  } else {
    throw new Error(`Missing coordinates for "${businessName}". Address available but coordinates not resolved yet.`);
  }
};

/**
 * Logs coordinate debugging information
 * @param {Object} businessLocation - Business location object
 * @param {Array} keywords - Keywords array
 * @param {string} component - Component name for logging
 */
export const logCoordinateDebug = (businessLocation, keywords = [], component = 'Map') => {
  const coordinates = extractBusinessCoordinates(businessLocation, keywords);
  
  if (coordinates) {
    console.log(`🎯 ${component} using business coordinates from ${coordinates.source}: ${coordinates.lat}, ${coordinates.lng}`);
    console.log(`📍 Business location object:`, businessLocation);
  } else {
    console.warn(`⚠️ ${component} using fallback coordinates - no valid business location or ranking data available`);
    console.log(`📍 ${component} BusinessLocation debug:`, {
      directLat: businessLocation?.lat,
      directLng: businessLocation?.lng,
      coordinatesLat: businessLocation?.coordinates?.lat,
      coordinatesLng: businessLocation?.coordinates?.lng,
      hasKeywords: keywords.length > 0,
      hasTestLocations: keywords[0]?.testLocations?.length || 0
    });
  }
};